import { Thing, ThingActivity, ThingType } from '@eagle/core-data-types';
import {
  CacheDataTypes,
  EntryDataBody,
  evaluate,
  EventLocationData,
  Icon,
  TRACK_EVENT_DIALOG_DEFAULT_TEMPLATE,
  useAuthenticated,
  useBoolFlag,
  useFetchAllCache,
  useFetchOneCache,
  useHistorySearch,
  usePromise,
  useUiTemplate,
} from '@eagle/react-common';
import { Box, IconButton, Stack, Tooltip, Typography } from '@ethings/ui-toolkit';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DateTime } from 'luxon';
import { FC, type CSSProperties, type JSX, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  filterExcludedTripHistoryEvents,
  getEventColor,
  getEventDisplayLabel,
  getThingRangeBoundaryEvents,
  getTripEventsInRange,
  getTripTimelineProcessedEvents,
  getVisibleTripTimelineEvents,
  isLocationUpdateEvent,
  isVisibleTripTimelineEvent,
  toEventTime,
} from '../trip-history-base-view-utils';
import { TripEventDrawerProps } from '../trip-history-base-view.types';
import { isTripHistoryMapItem } from '../trip-history-map-item-utils';
import { TripEventDrawerMediaSection } from './trip-event-drawer-media-section';

const EVENT_DIALOG_TEMPLATE_ID = 'track-event-dialog';
const EVENT_DIALOG_TEMPLATE_FLAG = 'track-ui-template-driven-event-dialog-temporary';
const EVENT_ACTIVITY_LOOKUP_WINDOW_HOURS = 24;

interface EventDialogTemplateContext {
  event: EventLocationData;
  trip?: {
    'thing-activity': {
      entityId: string;
      finish: string;
      id: string;
      isOngoing: boolean;
      journeyId: string;
      start: string;
    };
    durationMinutes: number;
    events: {
      distancePrimaryIncrease?: EventLocationData;
      distancePrimaryStart?: EventLocationData;
      distancePrimaryEnd?: EventLocationData;
      engineRunningStart?: EventLocationData;
      engineRunningEnd?: EventLocationData;
      fuelConsumed?: EventLocationData;
    };
  };
  ui: {
    actions: {
      nextDisabled: boolean;
      onClose: () => void;
      onNext: () => void;
      onPrevious: () => void;
      previousDisabled: boolean;
    };
    colors: {
      cardBorder: string;
      cardBackgroundColor: string;
      dividerColor: string;
      drawerBackgroundColor: string;
      eventColor: string;
    };
    content: {
      eventData: ReactNode;
      media: ReactNode;
    };
    labels: {
      close: string;
      eventData: string;
      media: string;
      next: string;
      previous: string;
      summaryText?: string;
      title: string;
    };
    layout: {
      drawerSpacing: number;
      eventTitleVariant: 'body1' | 'body2';
      headerSpacing: number;
      markerSize: number;
      sectionPadding: string;
      sectionTitleVariant: 'caption' | 'subtitle2';
    };
    styles: {
      drawer: CSSProperties;
      drawerStack: CSSProperties;
      header: CSSProperties;
      marker: CSSProperties;
      content: CSSProperties;
      eventDataCard: CSSProperties;
      eventDataSection: CSSProperties;
      mediaCard: CSSProperties;
    };
  };
  thing?: Thing;
  thingActivity?: ThingActivity;
  thingType?: ThingType;
}

export const TripEventDrawerContent: FC<TripEventDrawerProps> = ({ eventDefinitions, eventModeEvents, eventModeShowLocationUpdates = false, eventModeSortOrder = 'oldest-first', onClose, onSelectEvent, selectedEvent, selectedEventId, selectedTrip, selectedTripMetricEvents, tripEventSummarizationOptions }) => {
  const { t } = useTranslation(['common', 'track']);
  const theme = useTheme();
  const isDesktopWide = useMediaQuery(theme.breakpoints.up('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const useTemplateEventDialog = useBoolFlag(EVENT_DIALOG_TEMPLATE_FLAG);
  const { restClient } = useAuthenticated();
  const thingCache = useFetchOneCache(CacheDataTypes.THING);
  const thingTypeCache = useFetchAllCache(CacheDataTypes.THING_TYPE);
  const { loaded: templateLoaded, template } = useUiTemplate(
    EVENT_DIALOG_TEMPLATE_ID,
    TRACK_EVENT_DIALOG_DEFAULT_TEMPLATE as object,
    undefined,
    [],
    useTemplateEventDialog !== false,
  );
  const { eventData, setClickedEventId, showLocationUpdateEvents } = useHistorySearch();

  const drawerSpacing = isSmallScreen ? 1.25 : 1.5;
  const headerSpacing = isSmallScreen ? 1.5 : 2;
  const markerSize = isSmallScreen ? 12 : 14;
  const sectionPadding = isSmallScreen ? '8px 10px 10px 10px' : '10px 12px 12px 12px';
  const sectionTitleVariant: 'caption' | 'subtitle2' = isSmallScreen ? 'caption' : 'subtitle2';
  const eventTitleVariant: 'body2' | 'body1' = isSmallScreen ? 'body2' : 'body1';
  const drawerBackgroundColor = isDesktopWide ? theme.palette.background.default : theme.palette.background.paper;
  const tripEventData = selectedTrip
    ? eventData.find((item) => isTripHistoryMapItem(item, selectedTrip))
    : undefined;
  const showLocationUpdates = selectedTrip ? (showLocationUpdateEvents[selectedTrip.id] ?? false) : false;
  const isTimelineEventVisible = (event: EventLocationData): boolean => isVisibleTripTimelineEvent(event, eventDefinitions);
  const orderedEvents = selectedTrip
    ? (() => {
      const allTripEvents = filterExcludedTripHistoryEvents(getTripEventsInRange(selectedTrip, tripEventData?.data.items ?? []))
        .filter((event) => isTimelineEventVisible(event));
      const startEvent = allTripEvents[0];
      const endEvent = allTripEvents.length ? allTripEvents[allTripEvents.length - 1] : undefined;
      const visibleTimelineEvents = getVisibleTripTimelineEvents(selectedTrip, tripEventData?.data.items ?? [], showLocationUpdates)
        .filter((event) => isTimelineEventVisible(event));
      const processedTimelineEvents = getTripTimelineProcessedEvents(visibleTimelineEvents, tripEventSummarizationOptions)
        .map((entry) => {
          if (!entry.isSummarized || !entry.eventCount || entry.eventCount <= 1) return entry.event;

          return {
            ...entry.event,
            _eventCount: entry.eventCount,
            _isSummarized: true,
            _originalEvents: entry.originalEvents,
            _summarizationMode: entry.summarizationMode,
          } as EventLocationData;
        });

      return [
        startEvent,
        ...processedTimelineEvents,
        endEvent,
      ].filter((event, index, events): event is EventLocationData => {
        return Boolean(event?.eventId) && events.findIndex((item) => item?.eventId === event?.eventId) === index;
      });
    })()
    : (() => {
      const filteredModeEvents = filterExcludedTripHistoryEvents(eventModeEvents ?? [])
        .filter((event) => isTimelineEventVisible(event));
      const { end, start } = getThingRangeBoundaryEvents(filteredModeEvents);
      const boundaryEventIds = new Set([start?.eventId, end?.eventId].filter((eventId): eventId is string => Boolean(eventId)));
      const events = [...filteredModeEvents]
        .filter((event) => !boundaryEventIds.has(event.eventId))
        .filter((event) => eventModeShowLocationUpdates || !isLocationUpdateEvent(event))
        .sort((first, second) => toEventTime(first.occurred).toMillis() - toEventTime(second.occurred).toMillis());

      return eventModeSortOrder === 'oldest-first' ? events : events.reverse();
    })();

  const drawerEvent = (selectedEvent?.eventId === selectedEventId ? selectedEvent : null)
    ?? orderedEvents.find((event) => event.eventId === selectedEventId)
    ?? null;

  const selectEventByIndex = (index: number): void => {
    const event = orderedEvents[index];
    if (!event) return;
    setClickedEventId(event.eventId);
    onSelectEvent(event);
  };

  const closeDrawer = (): void => {
    setClickedEventId(null);
    onClose();
  };

  const [templateDomainContext] = usePromise<Pick<EventDialogTemplateContext, 'event' | 'thing' | 'thingActivity' | 'thingType'> | undefined>(async () => {
    if (!drawerEvent) {
      return undefined;
    }

    const baseContext = {
      event: drawerEvent,
      thing: undefined,
      thingActivity: undefined,
      thingType: undefined,
    };

    if (!useTemplateEventDialog || !drawerEvent.thingId) {
      return baseContext;
    }

    const thing = await thingCache.one<Thing>(drawerEvent.thingId);
    const thingType = thing?.thingTypeId
      ? await thingTypeCache.one<ThingType>(thing.thingTypeId)
      : undefined;

    const occurredAt = toEventTime(drawerEvent.occurred);
    const occurredMillis = occurredAt.toMillis();
    const rangeStart = selectedTrip
      ? selectedTrip.start.toJSDate()
      : occurredAt.minus({ hours: EVENT_ACTIVITY_LOOKUP_WINDOW_HOURS }).toJSDate();
    const rangeFinish = selectedTrip
      ? selectedTrip.finish.toJSDate()
      : occurredAt.plus({ hours: EVENT_ACTIVITY_LOOKUP_WINDOW_HOURS }).toJSDate();
    const activities = await restClient.thingActivity.fetchAllByThing(
      drawerEvent.thingId,
      rangeStart,
      rangeFinish,
    );
    const thingActivity = activities.find((activity): activity is ThingActivity => {
      if (!activity?.start) return false;
      const start = DateTime.fromJSDate(activity.start).toMillis();
      const finish = DateTime.fromJSDate(activity.finish ?? new Date()).toMillis();
      return start <= occurredMillis && occurredMillis <= finish;
    });

    return {
      event: drawerEvent,
      thing,
      thingActivity,
      thingType,
    };
  }, [
    drawerEvent,
    restClient.thingActivity,
    selectedTrip,
    thingCache,
    thingTypeCache,
    useTemplateEventDialog,
  ]);

  if (!drawerEvent) return <></>;

  if (useTemplateEventDialog === undefined) {
    return <Box data-testid="trip-event-detail-drawer-template-guard" style={{ backgroundColor: drawerBackgroundColor, height: '100%', minHeight: 0, opacity: 0 }} />;
  }

  const selectedEventIndex = orderedEvents.findIndex((event) => event.eventId === drawerEvent.eventId);
  const previousDisabled = selectedEventIndex <= 0;
  const nextDisabled = selectedEventIndex < 0 || selectedEventIndex >= orderedEvents.length - 1;
  const eventColor = getEventColor(drawerEvent, eventDefinitions);
  const summarizedDrawerEvent = drawerEvent as EventLocationData & {
    _eventCount?: number;
    _isSummarized?: boolean;
    _summarizationMode?: string;
  };
  const showSummarizedDetails = Boolean(
    summarizedDrawerEvent._isSummarized
    && summarizedDrawerEvent._eventCount
    && summarizedDrawerEvent._eventCount > 1
    && summarizedDrawerEvent._summarizationMode,
  );
  const summarizationMode = summarizedDrawerEvent._summarizationMode ?? 'first';

  const renderLegacyContent = (): JSX.Element => {
    return (
      <Box data-testid="trip-event-detail-drawer" style={{ backgroundColor: drawerBackgroundColor, height: '100%', minHeight: 0 }}>
        <Stack spacing={drawerSpacing} style={{ height: '100%', minHeight: 0, position: 'relative' }}>
          <Box
            style={{
              backgroundColor: drawerBackgroundColor,
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              zIndex: 2,
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              minHeight: '55px',
            }}
          >
            <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={headerSpacing}>
              <Stack alignItems="center" direction="row" spacing={1} style={{ minWidth: 0 }}>
                <Box
                  style={{
                    backgroundColor: eventColor,
                    borderRadius: '50%',
                    flexShrink: 0,
                    height: markerSize,
                    width: markerSize,
                  }}
                />
                <Stack spacing={0.25} style={{ minWidth: 0 }}>
                  <Typography style={{ fontWeight: 600 }} variant={eventTitleVariant}>{getEventDisplayLabel(drawerEvent, eventDefinitions)}</Typography>
                  {showSummarizedDetails && (
                    <Typography color="text.secondary" variant="body2">
                      <Trans
                        components={[<span key="occurrence-count" />]}
                        i18nKey={`track:page.history.showing-${summarizationMode}-count-of-occurrences.labels`}
                        values={{ count: summarizedDrawerEvent._eventCount }}
                      />
                    </Typography>
                  )}
                </Stack>
              </Stack>

              <Stack alignItems="center" direction="row" spacing={1}>
                <Tooltip title={t('common:common.action.previous')}>
                  <span>
                    <IconButton
                      data-testid="previous-button"
                      disabled={previousDisabled}
                      onClick={() => selectEventByIndex(selectedEventIndex - 1)}
                      size="small"
                    >
                      <Icon icon="ChevronLeft" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('common:common.action.next')}>
                  <span>
                    <IconButton
                      data-testid="next-button"
                      disabled={nextDisabled}
                      onClick={() => selectEventByIndex(selectedEventIndex + 1)}
                      size="small"
                    >
                      <Icon icon="ChevronRight" />
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton aria-label={t('common:common.action.close')} data-testid="event-detail-close-button" onClick={closeDrawer} size="small">
                  <Icon icon="CloseIcon" />
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          <Box style={{ flex: 1, minHeight: 0, paddingTop: 2 }}>
            <Stack spacing={drawerSpacing}>
              <Box
                style={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 10,
                  overflow: 'hidden',
                }}
              >
                <Stack spacing={1} style={{ padding: sectionPadding }}>
                  <Typography color="text.secondary" variant={sectionTitleVariant}>{t('track:page.history.event-dialog.event-data.labels')}</Typography>
                  <Box>
                    <EntryDataBody event={drawerEvent} />
                  </Box>
                </Stack>
              </Box>

              <Box
                style={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 10,
                  overflow: 'hidden',
                  padding: sectionPadding,
                }}
              >
                <Stack spacing={1}>
                  <Typography color="text.secondary" variant={sectionTitleVariant}>{t('track:page.history.labels.media')}</Typography>
                  <Box>
                    <TripEventDrawerMediaSection event={drawerEvent} />
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>
    );
  };

  if (!useTemplateEventDialog) {
    return renderLegacyContent();
  }

  const resolvedTemplate: unknown = templateLoaded && template
    ? template
    : TRACK_EVENT_DIALOG_DEFAULT_TEMPLATE;

  const trip = selectedTrip
    ? {
      'thing-activity': {
        entityId: selectedTrip.entityId,
        finish: selectedTrip.finish.toISO(),
        id: selectedTrip.id,
        isOngoing: selectedTrip.isOngoing,
        journeyId: selectedTrip.journeyId,
        start: selectedTrip.start.toISO(),
      },
      durationMinutes: Math.max(Math.round(selectedTrip.finish.diff(selectedTrip.start, 'minutes').minutes), 0),
      events: {
        distancePrimaryIncrease: selectedTripMetricEvents?.distancePrimaryIncreaseEvent,
        distancePrimaryStart: selectedTripMetricEvents?.odometerStartEvent,
        distancePrimaryEnd: selectedTripMetricEvents?.odometerEndEvent,
        engineRunningStart: selectedTripMetricEvents?.engineHoursStartEvent,
        engineRunningEnd: selectedTripMetricEvents?.engineHoursEndEvent,
        fuelConsumed: selectedTripMetricEvents?.fuelConsumedEvent,
      },
    } satisfies NonNullable<EventDialogTemplateContext['trip']>
    : undefined;

  const templateContext: EventDialogTemplateContext = {
    event: templateDomainContext?.event ?? drawerEvent,
    trip,
    thing: templateDomainContext?.thing,
    thingActivity: templateDomainContext?.thingActivity,
    thingType: templateDomainContext?.thingType,
    ui: {
      actions: {
        nextDisabled,
        onClose: closeDrawer,
        onNext: () => selectEventByIndex(selectedEventIndex + 1),
        onPrevious: () => selectEventByIndex(selectedEventIndex - 1),
        previousDisabled,
      },
      colors: {
        cardBorder: `1px solid ${theme.palette.divider}`,
        cardBackgroundColor: theme.palette.background.paper,
        dividerColor: theme.palette.divider,
        drawerBackgroundColor,
        eventColor,
      },
      content: {
        eventData: <EntryDataBody event={drawerEvent} />,
        media: <TripEventDrawerMediaSection event={drawerEvent} />,
      },
      labels: {
        close: t('common:common.action.close'),
        eventData: t('track:page.history.event-dialog.event-data.labels'),
        media: t('track:page.history.labels.media'),
        next: t('common:common.action.next'),
        previous: t('common:common.action.previous'),
        summaryText: showSummarizedDetails
          ? t(`track:page.history.showing-${summarizationMode}-count-of-occurrences.labels`, { count: summarizedDrawerEvent._eventCount })
          : undefined,
        title: getEventDisplayLabel(drawerEvent, eventDefinitions),
      },
      layout: {
        drawerSpacing,
        eventTitleVariant,
        headerSpacing,
        markerSize,
        sectionPadding,
        sectionTitleVariant,
      },
      styles: {
        drawer: {
          backgroundColor: drawerBackgroundColor,
          height: '100%',
          minHeight: 0,
        },
        drawerStack: {
          height: '100%',
          minHeight: 0,
          position: 'relative',
        },
        header: {
          backgroundColor: drawerBackgroundColor,
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 2,
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
          minHeight: '55px',
        },
        marker: {
          backgroundColor: eventColor,
          borderRadius: '50%',
          flexShrink: 0,
          height: markerSize,
          width: markerSize,
        },
        content: {
          flex: 1,
          minHeight: 0,
          paddingTop: 2,
        },
        eventDataCard: {
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 10,
          overflow: 'hidden',
        },
        eventDataSection: {
          padding: sectionPadding,
        },
        mediaCard: {
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 10,
          overflow: 'hidden',
          padding: sectionPadding,
        },
      },
    },
  };

  try {
    const content = evaluate(resolvedTemplate, templateContext);
    return <>{content ?? null}</>;
  } catch (error) {
    console.error('Trip event dialog template evaluation failed', error);
    return renderLegacyContent();
  }
};
